import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { UserProfileDetail } from '@/modules/user-profile/utils/transformers';

/**
 * User Profile state
 */
export interface UserProfileState {
  /** Current user profile data */
  profile: UserProfileDetail | null;
  /** Whether profile is being loaded */
  isLoading: boolean;
  /** Whether profile is being fetched (refetch) */
  isFetching: boolean;
  /** Error message if profile fetch failed */
  error: string | null;
}

const initialState: UserProfileState = {
  profile: null,
  isLoading: false,
  isFetching: false,
  error: null,
};

export const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    /**
     * Set user profile data
     */
    setProfile: (state, action: PayloadAction<UserProfileDetail | null>) => {
      state.profile = action.payload;
      state.error = null;
    },

    /**
     * Update profile fields (partial update)
     */
    updateProfile: (state, action: PayloadAction<Partial<UserProfileDetail>>) => {
      if (state.profile) {
        state.profile = {
          ...state.profile,
          ...action.payload,
        };
      }
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    /**
     * Set fetching state (for refetch operations)
     */
    setFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },

    /**
     * Set error message
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
      state.isFetching = false;
    },

    /**
     * Clear user profile state (on logout)
     */
    clearProfile: (state) => {
      state.profile = null;
      state.isLoading = false;
      state.isFetching = false;
      state.error = null;
    },
  },
});

export const {
  setProfile,
  updateProfile,
  setLoading,
  setFetching,
  setError,
  clearProfile,
} = userProfileSlice.actions;

// ============================================
// Selectors
// ============================================

/** Select the entire user profile state */
export const selectUserProfile = (state: RootState) => state.userProfile;

/** Select current user profile */
export const selectProfile = (state: RootState) => state.userProfile.profile;

/** Select if profile is loading */
export const selectIsProfileLoading = (state: RootState) =>
  state.userProfile.isLoading;

/** Select if profile is fetching */
export const selectIsProfileFetching = (state: RootState) =>
  state.userProfile.isFetching;

/** Select profile error */
export const selectProfileError = (state: RootState) => state.userProfile.error;

/** Select whether current user is an Admin (client mode only) */
export const selectIsAdmin = (state: RootState) =>
  state.userProfile.profile?.role === 'Admin';

export default userProfileSlice.reducer;

